/**
 * use-kanban-preferences.ts
 *
 * Preferenze di visualizzazione della board Kanban Planner, persistite in localStorage
 * per utente (copertine visibili, task completati visibili, stato espanso/compresso
 * della sezione "Completate" per singola colonna).
 */

import { useLocalStorage } from '@mantine/hooks';

interface KanbanPrefs {
  showCovers: boolean;
  showCompleted: boolean;
  completedExpanded: Record<number, boolean>;
}

const DEFAULT_PREFS: KanbanPrefs = {
  showCovers: true,
  showCompleted: false,
  completedExpanded: {},
};

function isCompletedExpandedFor(prefs: KanbanPrefs, columnId: number): boolean {
  return prefs.completedExpanded[columnId] ?? prefs.showCompleted;
}

export function useKanbanPreferences(userId?: number | string) {
  const [prefs, setPrefs] = useLocalStorage<KanbanPrefs>({
    key: `ufficio-stile-kanban-prefs-${userId ?? 'anon'}`,
    defaultValue: DEFAULT_PREFS,
    getInitialValueInEffect: false,
  });

  const isCompletedExpanded = (columnId: number): boolean => isCompletedExpandedFor(prefs, columnId);

  const toggleColumnCompleted = (columnId: number) =>
    setPrefs((p) => ({
      ...p,
      completedExpanded: {
        ...p.completedExpanded,
        [columnId]: !isCompletedExpandedFor(p, columnId),
      },
    }));

  const setShowCompleted = (v: boolean) =>
    setPrefs((p) => ({ ...p, showCompleted: v, completedExpanded: {} }));

  const setShowCovers = (v: boolean) => setPrefs((p) => ({ ...p, showCovers: v }));

  return {
    ...prefs,
    isCompletedExpanded,
    toggleColumnCompleted,
    setShowCompleted,
    setShowCovers,
  };
}
